import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Plus, Pencil, Trash2, Lock, Unlock, Save } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

export default function RolesPermissionsPage() {
  const { t } = useLanguage();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    data_scope: 'all'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, modulesRes] = await Promise.all([
        api.get('/roles'),
        api.get('/modules')
      ]);
      setRoles(rolesRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      // Initialiser les rôles par défaut si nécessaire
      try {
        await api.post('/roles/init-defaults');
        const rolesRes = await api.get('/roles');
        setRoles(rolesRes.data);
      } catch (e) {
        toast.error('Erreur de chargement des rôles');
      }
    } finally {
      setLoading(false);
    }
  };

  const initDefaultRoles = async () => {
    try {
      const res = await api.post('/roles/init-defaults');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name || !newRole.display_name) {
      toast.error('Nom et nom d\'affichage requis');
      return;
    }

    try {
      await api.post('/roles', newRole);
      toast.success('Rôle créé avec succès');
      setShowCreateDialog(false);
      setNewRole({ name: '', display_name: '', description: '', permissions: [], data_scope: 'all' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;

    try {
      await api.put(`/api/roles/${selectedRole.id}`, {
        display_name: selectedRole.display_name,
        description: selectedRole.description,
        permissions: selectedRole.permissions,
        data_scope: selectedRole.data_scope
      });
      toast.success('Rôle mis à jour');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system) {
      toast.error('Les rôles système ne peuvent pas être supprimés');
      return;
    }

    if (!confirm(`Supprimer le rôle "${role.display_name}" ?`)) return;

    try {
      await api.delete(`/api/roles/${role.id}`);
      toast.success('Rôle supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const togglePermission = (permission, isNew = false) => {
    if (isNew) {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    } else if (selectedRole) {
      setSelectedRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    }
  };

  const getDataScopeLabel = (scope) => {
    switch (scope) {
      case 'all': return 'Toutes les données';
      case 'own': return 'Ses propres données';
      case 'assigned': return 'Données assignées';
      default: return scope;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Gestion des Rôles et Permissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Configurez les droits d'accès pour chaque rôle
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={initDefaultRoles} data-testid="init-roles-btn">
            Initialiser rôles par défaut
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-role-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau rôle
          </Button>
        </div>
      </div>

      {/* Roles Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {roles.map(role => (
          <Card key={role.id} className="relative" data-testid={`role-card-${role.name}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {role.is_system ? (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Unlock className="h-4 w-4 text-green-500" />
                    )}
                    {role.display_name}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {role.description || 'Aucune description'}
                  </CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedRole({ ...role });
                      setShowEditDialog(true);
                    }}
                    data-testid={`edit-role-${role.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!role.is_system && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRole(role)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-role-${role.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium">Portée des données:</span>
                  <Badge variant="secondary" className="ml-2">
                    {getDataScopeLabel(role.data_scope)}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm font-medium">Permissions ({role.permissions?.length || 0}):</span>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {role.permissions?.slice(0, 5).map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs">
                        {modules.find(m => m.id === perm)?.name || perm}
                      </Badge>
                    ))}
                    {role.permissions?.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{role.permissions.length - 5}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modules Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Modules disponibles</CardTitle>
          <CardDescription>Liste des modules pouvant être assignés aux rôles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {modules.map(module => (
              <div key={module.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                <Badge variant="secondary">{module.id}</Badge>
                <div>
                  <p className="font-medium text-sm">{module.name}</p>
                  <p className="text-xs text-muted-foreground">{module.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouveau rôle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Identifiant (code)</Label>
                <Input
                  id="name"
                  value={newRole.name}
                  onChange={e => setNewRole({ ...newRole, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  placeholder="ex: surveillant"
                  data-testid="new-role-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name">Nom d'affichage</Label>
                <Input
                  id="display_name"
                  value={newRole.display_name}
                  onChange={e => setNewRole({ ...newRole, display_name: e.target.value })}
                  placeholder="ex: Surveillant"
                  data-testid="new-role-display-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newRole.description}
                onChange={e => setNewRole({ ...newRole, description: e.target.value })}
                placeholder="Description du rôle..."
                data-testid="new-role-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Portée des données</Label>
              <Select
                value={newRole.data_scope}
                onValueChange={value => setNewRole({ ...newRole, data_scope: value })}
              >
                <SelectTrigger data-testid="new-role-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les données</SelectItem>
                  <SelectItem value="own">Ses propres données uniquement</SelectItem>
                  <SelectItem value="assigned">Données assignées uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg">
                {modules.map(module => (
                  <div key={module.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`new-${module.id}`}
                      checked={newRole.permissions.includes(module.id)}
                      onCheckedChange={() => togglePermission(module.id, true)}
                      data-testid={`new-perm-${module.id}`}
                    />
                    <label htmlFor={`new-${module.id}`} className="text-sm cursor-pointer">
                      {module.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateRole} data-testid="save-new-role">
              <Save className="h-4 w-4 mr-2" />
              Créer le rôle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Modifier le rôle: {selectedRole?.display_name}
              {selectedRole?.is_system && (
                <Badge variant="secondary" className="ml-2">Système</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedRole && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Identifiant</Label>
                  <Input value={selectedRole.name} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_display_name">Nom d'affichage</Label>
                  <Input
                    id="edit_display_name"
                    value={selectedRole.display_name}
                    onChange={e => setSelectedRole({ ...selectedRole, display_name: e.target.value })}
                    disabled={selectedRole.name === 'admin'}
                    data-testid="edit-role-display-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_description">Description</Label>
                <Input
                  id="edit_description"
                  value={selectedRole.description || ''}
                  onChange={e => setSelectedRole({ ...selectedRole, description: e.target.value })}
                  data-testid="edit-role-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Portée des données</Label>
                <Select
                  value={selectedRole.data_scope || 'all'}
                  onValueChange={value => setSelectedRole({ ...selectedRole, data_scope: value })}
                  disabled={selectedRole.name === 'admin'}
                >
                  <SelectTrigger data-testid="edit-role-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les données</SelectItem>
                    <SelectItem value="own">Ses propres données uniquement</SelectItem>
                    <SelectItem value="assigned">Données assignées uniquement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg">
                  {modules.map(module => (
                    <div key={module.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-${module.id}`}
                        checked={selectedRole.permissions?.includes(module.id)}
                        onCheckedChange={() => togglePermission(module.id)}
                        disabled={selectedRole.name === 'admin'}
                        data-testid={`edit-perm-${module.id}`}
                      />
                      <label htmlFor={`edit-${module.id}`} className="text-sm cursor-pointer">
                        {module.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateRole} disabled={selectedRole?.name === 'admin'} data-testid="save-edit-role">
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
